import useOnboardingStorage from 'hooks/useOnboardingStorage';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import LegalConsent from 'storybookConfig/stories/molecules/LegalConsent';
import ProgressBar from 'storybookConfig/stories/organisms/ProgressBar';
import { CommerceType, Platform } from 'types/models/company';
import { friendlyPlatformName } from 'utils/platform';
import { getDomainFromUrl } from 'utils/strings';
import { FooterRow, HeaderContent, ReviewItem, SectionContent, StepContent } from './Shared.styles';

interface ReviewProps {
  currentStep: number;
  totalSteps: number;
  onBackButtonClick: () => void;
  isShopifyInstall: boolean;
}

const Review = ({ currentStep, totalSteps, onBackButtonClick, isShopifyInstall }: ReviewProps) => {
  const { watch, reset, formState } = useFormContext();
  const { setOnboardingStorage } = useOnboardingStorage();

  const onClickSubmit = () => {
    setOnboardingStorage(undefined);
  };

  const onClickStartOver = useCallback(() => {
    if (isShopifyInstall) {
      // Keep Shopify Store URL
      reset({
        commerceType: CommerceType.Seller,
        platform: Platform.Shopify,
      });
    } else {
      reset({
        commerceType: CommerceType.Seller,
        platform: Platform.Shopify,
        storeUrl: undefined,
      });
    }
    onBackButtonClick();
  }, [isShopifyInstall, onBackButtonClick, reset]);

  const commerceType = watch('commerceType');
  const platform = watch('platform');
  const storeUrl = watch('storeUrl');

  return (
    <StepContent>
      <ProgressBar totalSteps={totalSteps} completedSteps={currentStep} stepLabel="Review" />

      <SectionContent>
        <HeaderContent>
          <Heading variant="Headings/H3" as="h1">
            Let&apos;s review your dropship settings
          </Heading>

          <Body as="p">Please double-check to confirm this information is accurate.</Body>
        </HeaderContent>

        <ReviewItem>
          <Body variant="Body/Header" color="bodyTextSecondary">
            You&apos;d like to use Carro to
          </Body>

          <Heading variant="Headings/H4" as="strong">
            {commerceType === CommerceType.Buyer
              ? 'Buy products from a supplier'
              : 'Sell products to a retailer'}
          </Heading>
        </ReviewItem>

        <ReviewItem>
          <Body variant="Body/Header" color="bodyTextSecondary">
            You&apos;d like to connect to Carro via
          </Body>

          <Heading variant="Headings/H4" as="strong">
            {friendlyPlatformName(platform, commerceType)}
          </Heading>
        </ReviewItem>

        {storeUrl && (
          <ReviewItem>
            <Body variant="Body/Header" color="bodyTextSecondary">
              Your store URL is
            </Body>

            <Heading variant="Headings/H4" as="strong">
              https://{getDomainFromUrl(storeUrl)}
            </Heading>
          </ReviewItem>
        )}

        {commerceType === CommerceType.Seller && <LegalConsent />}
      </SectionContent>

      <FooterRow>
        <TertiaryButton
          $iconName="navigate_before"
          $removePadding="left"
          onClick={onClickStartOver}
        >
          Start Over
        </TertiaryButton>

        <PrimaryButton
          $iconAfter
          type="submit"
          disabled={formState.isSubmitting}
          onClick={onClickSubmit}
        >
          {formState.isSubmitting ? 'Saving...' : 'Confirm Settings'}
        </PrimaryButton>
      </FooterRow>
    </StepContent>
  );
};

export default Review;
